import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import PropTypes from "prop-types";
import { Checkbox } from "@mui/material";

import { A, H1, H2, H3 } from "commons";

import { register } from "../../components/DataOperations";
import { GlobalMsg, GoLogo, LiveChat, Support } from "../../components";
import { routes } from "../../helpers/RouteHelpers";
import { TermsAndConditionsLink } from "./PlatformLinks";
import {
    AuthButton,
    AuthBlock,
    AuthContentHolder,
    AuthTextInput,
    ButtonLoading,
    Form,
    PasswordInput
} from "./AuthComponents";

export function Register({ isLoading }) {
    const [username, setUsername] = useState("");
    const [regCode, setRegCode] = useState("");
    const [password, setPassword] = useState("");
    const [terms, setTerms] = useState(false);

    const handleSubmit = e => {
        e.preventDefault();
        register(username, regCode, password, terms);
    };

    const isSubmitDisabled = !username || !password || !regCode || !terms;

    return (
        <AuthContentHolder>
            <GlobalMsg />
            <AuthBlock>
                <GoLogo />
                <H1 secondary>Registration</H1>

                <Instructions data-testid="register-instructions">
                    <span>To register, you&apos;ll need access to:</span>
                    <Ul>
                        <li>
                            <span>
                                the <BoldText>username</BoldText> in the &apos;Nucleus web login
                                information&apos; sent to your email address
                            </span>
                        </li>
                        <li>
                            <span>
                                the <BoldText>registration code</BoldText> sent to you in the letter
                                &apos;Your Nucleus login details&apos;
                            </span>
                        </li>
                    </Ul>
                </Instructions>

                <H2 textAlign="center" secondary>
                    Enter your details
                </H2>
                <Form onSubmit={handleSubmit}>
                    <AuthTextInput
                        data-testid="username"
                        label="Username"
                        value={username}
                        type="text"
                        onChange={e => setUsername(e.target.value)}
                        autoComplete="off"
                        autoCapitalize="none"
                    />

                    <AuthTextInput
                        data-testid="registration-code"
                        label="Registration code"
                        value={regCode}
                        type="text"
                        onChange={e => setRegCode(e.target.value)}
                        autoComplete="off"
                    />

                    <PasswordInput setPassword={setPassword} autocompleteDisabled={true} />

                    <TermsContainer>
                        <Checkbox
                            data-testid="terms-checkbox"
                            color="secondary"
                            onChange={() => setTerms(v => !v)}
                            checked={terms}
                        />
                        <span>
                            I have read and agreed to the{" "}
                            <A target="_blank" href={TermsAndConditionsLink} secondary>
                                Terms and Conditions
                            </A>{" "}
                            of using this service
                        </span>
                    </TermsContainer>

                    <ButtonLoading suppressClassNameWarning displayWhen={!isLoading} size={25}>
                        <AuthButton data-testid="submit-button" disabled={isSubmitDisabled}>
                            Submit
                        </AuthButton>
                    </ButtonLoading>

                    <BackLink textAlign="center" secondary>
                        <Link to={routes.public.login()}>Back to login?</Link>
                    </BackLink>
                </Form>
                <Support />
                <LiveChat data-testid="live-chat" />
            </AuthBlock>
        </AuthContentHolder>
    );
}

const TermsContainer = styled.div`
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
`;

const Ul = styled.ul`
    font-size: inherit;
    margin-left: 20px !important;
    margin-top: 10px !important;
`;

const BackLink = styled(H3)`
    cursor: pointer;
    margin-top: 20px;
`;

const Instructions = styled.div`
    margin-top: 20px;
    margin-left: 10px;
    text-align: initial;
`;

const BoldText = styled.b`
    font-weight: 600;
`;

Register.propTypes = {
    isLoading: PropTypes.bool
};
